"use client"

import type { Dispatch, RootState } from "@redux/store"
import type { CustomerData } from "@shopify/types"

import Backdrop from "@components/Backdrop"
import Cart from "@components/Cart/hoc/withStore"
import CartToast from "@components/CartToast/hoc/withStore"
import ExploreMenu from "@components/ExploreMenu"
import Footer from "@components/Footer/hoc/withContentfulData"
import HappyFox from "@components/HappyFox"
import Header from "@components/Header"
import GlobalDataContext from "@components/Layout/GlobalDataContext"
import MegaMenu from "@components/MegaMenu"
import Menu from "@components/Menu"
import MenuContext from "@components/Menu/MenuContext"
import Search from "@components/Search/hoc/withMatchSorter"
import breakpoints from "@constants/breakpoints"
import {
  ELEVAR_COOKIES_KEY,
  PASSED_DISCOUNT_KEY,
  SELECTED_PRODUCTS_KEY,
  UTM_PARAMS_KEY,
  VISITOR_INFO_KEY,
} from "@constants/storageKeys"
import useElevarEvents, {
  INTERNAL_USER_DATA_PUSHED_FOR_ELEVAR,
} from "@hooks/useElevarEvents"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import useScrollHandler from "@hooks/useScrollHandler"
import { selectCartQuantity, selectCartTotal } from "@redux/selectors"
import { getRewardsBalance } from "@utils/api"
import { getDecodedShopifyCustomerId, getIn } from "@utils/index"
import megaMenuConfig from "@utils/megaMenuConfig"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"
import { getShopURLForCollection, getShopURLForSeries } from "@utils/urls"
import React, { useEffect } from "react"
import { connect } from "react-redux"
import styled, { css } from "styled-components"

const getMainStyles = () => {
  const mobileOffset = 56 + 48
  const tabletOffset = 72 + 48
  const desktopOffset = 88 + 36
  return css`
    min-height: calc(100vh - ${mobileOffset}px - 410px);
    margin-top: ${mobileOffset}px;

    @media ${breakpoints.tablet} {
      min-height: calc(100vh - ${tabletOffset}px - 956px);
      margin-top: ${tabletOffset}px;
    }

    @media ${breakpoints.laptop} {
      min-height: calc(100vh - ${desktopOffset}px - 704px);
      margin-top: ${desktopOffset}px;
    }
  `
}

const MegaMenuContainer = styled.div<{ topOffset: number }>`
  ${({ topOffset }) => css`
    z-index: 100;
    position: fixed;
    top: ${topOffset}px;
    left: 0;
  `}
`

const Main = styled.main`
  ${getMainStyles()}
`

const YotpoCustomerContainer = styled.div`
  display: none;
`

interface LayoutProps {
  automaticDiscountsFetched: boolean
  cartTotal: null | string
  checkIfMigratedToCartAPI: () => void
  checkoutDataFetched: boolean
  checkoutId: null | string
  children?: React.ReactNode
  countdownTimer?: {
    eventEnd?: string
    eventStart: string
    textAfterEvent?: string
    textBeforeEvent: string
    textDuringEvent?: string
  }
  customerAccessToken: null | string
  customerCountryCode: string
  customerData: CustomerData
  customerDataFetched: boolean
  customerLocationFetched: boolean
  customerZipCode: string
  data?: any
  fetchAutomaticDiscounts: () => void
  fetchCheckout: (checkoutId: null | string) => void
  fetchCustomerData: (any) => void
  fetchCustomerLocation: () => void
  FixedHeaderComponent?: React.ReactNode
  fixedHeaderTopRef?: any
  location?: any
  logoutCustomer: (customerAccessToken: null | string) => void
  migratedToCartAPIChecked: boolean
  productCount: number
  products: any
  searchValue: string
  showFixedHeader?: boolean
}

const Layout = (props: LayoutProps) => {
  React.useEffect(() => {
    if (props.checkIfMigratedToCartAPI) {
      props.checkIfMigratedToCartAPI()
    }
  }, [props.checkIfMigratedToCartAPI])

  const [cart, setCart] = React.useState(false)
  const [menu, setMenu] = React.useState(false)
  const [menuConfig, setMenuConfig] = React.useState<
    | {
        brand?: string
        deviceType?: string
        showShopOnly?: boolean
      }
    | undefined
  >(undefined)
  const [rewardPoints, setRewardPoints] = React.useState<number | undefined>()

  const { elevarOnUserData } = useElevarEvents()

  React.useEffect(() => {
    if (!props.customerDataFetched) {
      props.fetchCustomerData({
        customerAccessToken: props.customerAccessToken,
      })
    }
  }, [props.customerAccessToken])

  const [elevarState, setElevarState] = React.useState<any>({})

  React.useEffect(() => {
    if (elevarState.pathname !== props.location.pathname) {
      setElevarState({
        pathname: props.location.pathname,
        userDataPushed: false,
      })
    }
  }, [props.location.pathname])

  const getBaseUserData = () => {
    const elevarProducts = props.products.map((product, i) => {
      const selectedProducts = getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
      const list = selectedProducts[product.shopifyProductId] || "direct"

      return {
        brand: "Urban Armor Gear",
        compareAtPrice: product.compareAtPrice || "0.0",
        device: product.device,
        deviceType: product.deviceType,
        image: product.image,
        list,
        position: i + 1,
        price: product.price,
        productId: product.shopifyProductId,
        productName: product.productName,
        quantity: product.quantity,
        sku: product.sku,
        variantId: product.shopifyVariantId,
        variantName: product.variantName,
      }
    })

    return {
      cartTotal: props.cartTotal || "0.00",
      consent: true, // TODO
      products: elevarProducts,
    }
  }

  React.useEffect(() => {
    const sendUserDataOnce = (data) => {
      const dataLayer = getIn(window, "dataLayer", [])
      const lastUserDataEvent = dataLayer
        .slice()
        .reverse()
        .find(
          ({ event }) => event && event === INTERNAL_USER_DATA_PUSHED_FOR_ELEVAR
        )
      const userDataPushed =
        lastUserDataEvent &&
        lastUserDataEvent.location === props.location.pathname
      if (!userDataPushed) {
        elevarOnUserData({ ...data })
      }
    }

    if (!elevarState.userDataPushed) {
      if (
        props.customerAccessToken &&
        props.checkoutId &&
        props.customerDataFetched &&
        props.checkoutDataFetched
      ) {
        const eventData: any = getBaseUserData()

        if (props.customerData?.id && props.customerData?.email) {
          eventData.id = props.customerData.id
          eventData.email = props.customerData.email
          eventData.loggedIn = true
        }
        sendUserDataOnce(eventData)
        setElevarState({ ...elevarState, userDataPushed: true })
      } else if (
        props.customerAccessToken &&
        !props.checkoutId &&
        props.customerDataFetched
      ) {
        const eventData: any = getBaseUserData()

        if (props.customerData?.id && props.customerData?.email) {
          eventData.id = props.customerData.id
          eventData.email = props.customerData.email
          eventData.loggedIn = true
        }

        sendUserDataOnce(eventData)
        setElevarState({ ...elevarState, userDataPushed: true })
      } else if (
        !props.customerAccessToken &&
        props.checkoutId &&
        props.checkoutDataFetched
      ) {
        const eventData: any = getBaseUserData()
        sendUserDataOnce(eventData)
        setElevarState({ ...elevarState, userDataPushed: true })
      } else if (!props.customerAccessToken && !props.checkoutId) {
        const eventData: any = getBaseUserData()
        sendUserDataOnce(eventData)
        setElevarState({ ...elevarState, userDataPushed: true })
      }
    }
  }, [
    props.location.pathname,
    props.customerData,
    props.customerDataFetched,
    props.checkoutId,
    props.checkoutDataFetched,
    props.products,
  ])

  React.useEffect(() => {
    if (!props.customerLocationFetched) {
      props.fetchCustomerLocation()
    }
  }, [])

  React.useEffect(() => {
    if (
      props.migratedToCartAPIChecked &&
      !props.checkoutDataFetched &&
      props.checkoutId
    ) {
      props.fetchCheckout(props.checkoutId)
    }
  }, [
    props.migratedToCartAPIChecked,
    props.checkoutDataFetched,
    props.checkoutId,
  ])

  React.useEffect(() => {
    if (!props.automaticDiscountsFetched) {
      props.fetchAutomaticDiscounts()
    }
  }, [])

  const fetchRewardsBalance = async () => {
    const rewardsBalance = await getRewardsBalance({
      shopifyCustomerId: props.customerData.id,
    })

    setRewardPoints(rewardsBalance?.data?.balance?.available || 0)
  }

  React.useEffect(() => {
    if (props.customerDataFetched && props.customerData?.id) {
      fetchRewardsBalance()
    }
  }, [props.customerDataFetched, props.customerData])

  const openMenu = (config?) => {
    setMenu(true)

    if (config) {
      setMenuConfig(config)
    }
  }

  const closeMenu = () => {
    setMenu(false)
    setMenuConfig(undefined)
  }

  const [search, setSearch] = React.useState(false)

  const openSearch = (e) => {
    e.stopPropagation()
    setSearch(true)
  }

  const closeSearch = () => {
    setSearch(false)
  }

  const filters = getIn(props, "data.filterData.filters")
  const contentfulProduct = getIn(props, "data.contentfulProduct")
  const contentfulPrintedDesign = getIn(props, "data.contentfulPrintedDesign")
  const {
    enterpriseLink,
    exploreMenuExploreEntryTypes,
    exploreMenuProductSeries,
    exploreMenuProductSeriesHeader,
    promos,
    promoScrollDelay,
    showPromoBanner,
    supportLink,
  } = getIn(props, "data.globalData", { menuItems: [] })
  const { countryProhibitedProducts } = getIn(
    props,
    "data.contentfulGlobalSettings",
    {}
  )

  const [renderedPromos, setRenderedPromos] = React.useState<any>([])

  React.useEffect(() => {
    if (promos && props.customerLocationFetched) {
      if (props.customerCountryCode && props.customerCountryCode === "US") {
        const filteredPromos = promos.filter(
          ({ internationalOnly, usOnly }) => {
            return !!usOnly || !internationalOnly
          }
        )

        setRenderedPromos(filteredPromos)
      } else if (
        props.customerCountryCode &&
        props.customerCountryCode !== "US"
      ) {
        const filteredPromos = props.data.globalData.promos.filter(
          ({ internationalOnly, specificCountries, usOnly }) => {
            if (specificCountries && specificCountries.length) {
              return specificCountries.includes(props.customerCountryCode)
            } else {
              return !usOnly || !!internationalOnly
            }
          }
        )

        setRenderedPromos(filteredPromos)
      }
    }
  }, [props.customerLocationFetched, promos])

  const [collapsePromoBanner, setCollapsePromoBanner] =
    React.useState<boolean>(false)

  const handleCollapsePromoBanner = () => {
    if (showPromoBanner && renderedPromos.length) {
      const html = document.getElementsByTagName("html")

      if (html && html[0] && html[0].getBoundingClientRect().top < 50 * -1) {
        setCollapsePromoBanner(true)
      } else {
        setCollapsePromoBanner(false)
      }
    }
  }

  const [shortenDesktopHeader, setShortenDesktopHeader] = React.useState(false)

  const handleShortenHeight = () => {
    const html = document.getElementsByTagName("html")
    if (html && html[0] && html[0].getBoundingClientRect().top < -50) {
      setShortenDesktopHeader(true)
    } else {
      setShortenDesktopHeader(false)
    }
  }

  const scrollHandler = () => {
    handleCollapsePromoBanner()
    handleShortenHeight()
  }

  useScrollHandler({
    onScroll: scrollHandler,
    runForProps: [renderedPromos],
  })

  const [activeMenuTab, setActiveMenuTab] = React.useState<string | undefined>(
    undefined
  )
  const clearActiveMenuTab = () => {
    setActiveMenuTab(undefined)
  }

  // megamenu-v2: Move config to Contentful
  const megaMenuItems = React.useMemo(() => {
    if (!megaMenuConfig) {
      return []
    }

    return megaMenuConfig.types.map((type) => {
      const typeData = megaMenuConfig.typeData[type]
      const menuItem: any = {
        label: typeData.label,
        slug: type,
      }

      if (typeData.link) {
        menuItem.link = typeData.link
      } else {
        menuItem.onClick = () => {
          if (type === activeMenuTab) {
            clearActiveMenuTab()
          } else {
            setActiveMenuTab(type)
          }
        }
      }

      return menuItem
    })
  }, [megaMenuConfig])

  const megaMenuRef: any = React.useRef(null)
  const megaMenuHeaderRef: any = React.useRef(null)

  useModalBackgroundClose({
    excludeRefs: [megaMenuHeaderRef],
    isOpen: !!activeMenuTab,
    modalRef: megaMenuRef,
    onRequestClose: clearActiveMenuTab,
  })

  const hasCountdownTimer = !!props.countdownTimer
  const megaMenuOffset =
    hasCountdownTimer && shortenDesktopHeader
      ? 64 + 56
      : shortenDesktopHeader
        ? 64
        : hasCountdownTimer
          ? 56 + 36 + 88
          : 36 + 88

  const exploreMenuRef: any = React.useRef(null)
  const [showExploreMenu, setShowExploreMenu] = React.useState<boolean>(false)

  const toggleExploreMenu = (e) => {
    e.stopPropagation()
    setShowExploreMenu(!showExploreMenu)
  }

  const closeExploreMenu = () => {
    setShowExploreMenu(false)
  }

  const exploreEntryTypes = React.useMemo(() => {
    if (!exploreMenuExploreEntryTypes || !exploreMenuExploreEntryTypes.length) {
      return []
    }

    return exploreMenuExploreEntryTypes.map(({ description, name, slug }) => ({
      description,
      link: slug,
      name,
    }))
  }, [exploreMenuExploreEntryTypes])

  const exploreProductSeries = React.useMemo(() => {
    if (!exploreMenuProductSeries || !exploreMenuProductSeries.length) {
      return []
    }

    return exploreMenuProductSeries.map(
      ({ __typename, color, icon, image, name, slug }) => ({
        color,
        icon: getIn(icon, "file.url"),
        image: getIn(image, "file.url"),
        link:
          __typename === "ContentfulCollection"
            ? getShopURLForCollection(slug)
            : getShopURLForSeries(slug),
        name,
      })
    )
  }, [exploreMenuProductSeries])

  useModalBackgroundClose({
    isOpen: showExploreMenu,
    modalRef: exploreMenuRef,
    onRequestClose: closeExploreMenu,
  })

  const setUTMParams = () => {
    if (window) {
      const location = getIn(window, "location")
      const params = new URLSearchParams(location.search)

      if (params) {
        const utmParams = {
          utm_campaign: params.get("utm_campaign"),
          utm_content: params.get("utm_content"),
          utm_medium: params.get("utm_medium"),
          utm_source: params.get("utm_source"),
          utm_term: params.get("utm_term"),
        }

        const storedUTMParams = getLocalStorage(UTM_PARAMS_KEY)
        const utmParamsToUpdate = Object.keys(utmParams).filter(
          (k) => !!utmParams[k]
        )

        if (utmParamsToUpdate.length) {
          const updatedUTMParams = {
            ...storedUTMParams,
          }

          utmParamsToUpdate.forEach((k) => {
            updatedUTMParams[k] = utmParams[k]
          })

          writeLocalStorage(UTM_PARAMS_KEY, updatedUTMParams)
        }
      }
    }
  }

  const setPassedCoupon = () => {
    if (window) {
      const location = getIn(window, "location")
      const params = new URLSearchParams(location.search)

      const passedCoupon = params.get("coupon")
      const storedPassedCoupon = getLocalStorage(PASSED_DISCOUNT_KEY)

      if (passedCoupon && passedCoupon !== storedPassedCoupon) {
        writeLocalStorage(PASSED_DISCOUNT_KEY, passedCoupon)
      }
    }
  }

  const setVisitorInfo = () => {
    if (window) {
      const location = getIn(window, "location")
      const params = new URLSearchParams(location.search)

      const visitorInfo = {
        fbclid: params.get("fbclid"),
        gclid: params.get("gclid"),
        utm_campaign: params.get("utm_campaign"),
        utm_content: params.get("utm_content"),
        utm_medium: params.get("utm_medium"),
        utm_source: params.get("utm_source"),
        utm_term: params.get("utm_term"),
      }

      const storedVisitorInfo = getLocalStorage(VISITOR_INFO_KEY)
      const visitorInfoToUpdate = Object.keys(visitorInfo).filter(
        (k) => !!visitorInfo[k]
      )

      if (visitorInfoToUpdate.length) {
        const updatedVisitorInfo = {
          ...storedVisitorInfo,
        }

        visitorInfoToUpdate.forEach((k) => {
          updatedVisitorInfo[k] = visitorInfo[k]
        })

        writeLocalStorage(VISITOR_INFO_KEY, updatedVisitorInfo)
      }
    }
  }

  const setElevarCookies = async () => {
    if (window) {
      const Window = window as any
      if (Window && Window.cookieStore) {
        const _elevar__ga = await Window.cookieStore.get("_ga")
        const _elevar__fbp = await Window.cookieStore.get("_fbp")
        const _elevar__fbc = await Window.cookieStore.get("_fbc")
        const elevarCookies = {
          _elevar__fbc,
          _elevar__fbp,
          _elevar__ga,
        }
        const storedElevarCookies = getLocalStorage(ELEVAR_COOKIES_KEY)
        const elevarCookiesToUpdate = Object.keys(elevarCookies).filter(
          (k) => !!elevarCookies[k]
        )

        if (elevarCookiesToUpdate.length) {
          const updatedElevarCookies = {
            ...storedElevarCookies,
          }

          elevarCookiesToUpdate.forEach((k) => {
            updatedElevarCookies[k] = elevarCookies[k].value
          })

          writeLocalStorage(ELEVAR_COOKIES_KEY, updatedElevarCookies)
        }
      }
    }
  }

  const [storageLoaded, setStorageLoaded] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (!storageLoaded) {
      setStorageLoaded(true)
      setUTMParams()
      setPassedCoupon()
      setVisitorInfo()
      setElevarCookies()
    }
  }, [])

  const cartToastYPosition = shortenDesktopHeader
    ? 64
    : showPromoBanner
      ? 120
      : 88

  const automaticDiscountData = getIn(
    props,
    "data.globalData.automaticDiscounts",
    []
  )

  useEffect(() => {
    if (props.customerData?.id) {
      ;(window as any).maestraRewardsExtension = {
        customer: {
          email: props.customerData.email,
          id: props.customerData.id,
          name: props.customerData.firstName,
        },
      }
    }
  }, [props.customerData])

  return (
    <>
      <Header
        collapsePromoBanner={collapsePromoBanner}
        countdownTimer={props.countdownTimer}
        enterpriseLink={enterpriseLink}
        filters={filters}
        FixedHeaderComponent={getIn(props, "FixedHeaderComponent", undefined)}
        fixedHeaderTopRef={getIn(props, "fixedHeaderTopRef", undefined)}
        isLoggedIn={!!props.customerData?.id}
        megaMenuActiveItem={activeMenuTab}
        megaMenuHeaderItems={megaMenuItems}
        megaMenuHeaderRef={megaMenuHeaderRef}
        onClickCart={(e) => {
          e.stopPropagation()
          setCart(true)
        }}
        onClickExplore={toggleExploreMenu}
        onClickMenu={(e) => {
          e.stopPropagation()
          openMenu()
        }}
        onClickSearch={openSearch}
        onLogout={() => {
          props.logoutCustomer(props.customerAccessToken)
          setMenu(false)
        }}
        product={contentfulProduct || contentfulPrintedDesign}
        productCount={getIn(props, "productCount", 0)}
        promos={renderedPromos}
        promoScrollDelay={promoScrollDelay || "5000"}
        rewardsPoints={rewardPoints}
        searchValue={getIn(props, "searchValue", "")}
        shortenDesktopHeader={shortenDesktopHeader}
        showFilters={
          contentfulProduct !== undefined ||
          contentfulPrintedDesign !== undefined
        }
        showFixedHeader={getIn(props, "showFixedHeader", undefined)}
        showPromoBanner={showPromoBanner}
        showRewardsPoints={!!props.customerData?.id}
        supportLink={supportLink}
      />
      <Backdrop isOpen={!!activeMenuTab} topOffset={megaMenuOffset}>
        <MegaMenuContainer ref={megaMenuRef} topOffset={megaMenuOffset}>
          <MegaMenu
            activeItem={activeMenuTab}
            data={getIn(props, "data.menuData", {})}
            megaMenuLinks={getIn(props, "data.globalData.megaMenuLinks", [])}
            onRequestClose={clearActiveMenuTab}
            topOffset={megaMenuOffset}
          />
        </MegaMenuContainer>
      </Backdrop>
      <Backdrop isOpen={showExploreMenu} topOffset={megaMenuOffset}>
        <MegaMenuContainer ref={exploreMenuRef} topOffset={megaMenuOffset}>
          <ExploreMenu
            exploreEntryTypes={exploreEntryTypes}
            onRequestClose={closeExploreMenu}
            productSeries={exploreProductSeries}
            productSeriesHeader={exploreMenuProductSeriesHeader}
            topOffset={megaMenuOffset}
          />
        </MegaMenuContainer>
      </Backdrop>
      <GlobalDataContext.Provider
        value={{
          countryProhibitedProducts,
          showPromoBanner,
        }}
      >
        <MenuContext.Provider value={{ openMenu }}>
          <Main id="mainContent">{props.children}</Main>
        </MenuContext.Provider>
      </GlobalDataContext.Provider>
      <Footer user={props.customerData.firstName || "Log In"} />
      <Menu
        config={menuConfig}
        data={props.data}
        filterFunnel={getIn(props, "data.menuData.filterFunnel", {})}
        globalData={getIn(props, "data.globalData", {})}
        isLoggedIn={!!props.customerData?.id}
        isOpen={menu}
        menuData={getIn(props, "data.menuData", {})}
        onLogout={() => {
          props.logoutCustomer(props.customerAccessToken)
          setMenu(false)
        }}
        onRequestClose={closeMenu}
        rewardsPoints={rewardPoints}
      />
      <Search
        automaticDiscountData={automaticDiscountData}
        isOpen={search}
        onRequestClose={closeSearch}
        searchSuggestions={getIn(
          props,
          "data.globalData.searchSuggestions",
          []
        )}
      />
      <Cart
        automaticDiscountData={automaticDiscountData}
        isOpen={cart}
        onRequestClose={() => {
          setCart(false)
        }}
      />
      <CartToast
        onClickCart={() => setCart(true)}
        yPosition={`${cartToastYPosition}px`}
      />
      {props.customerData?.id && props.customerData?.email && (
        <YotpoCustomerContainer
          data-authenticated="true"
          data-email={props.customerData.email}
          data-id={getDecodedShopifyCustomerId(props.customerData.id)}
          id="swell-customer-identification"
        />
      )}
      <HappyFox />
    </>
  )
}

const mapState = (state: RootState) => ({
  automaticDiscountsFetched: state.discounts.automaticDiscountsFetched,
  cartTotal: selectCartTotal(state),
  checkoutDataFetched: state.checkout.checkoutDataFetched,
  checkoutId: state.checkout.checkoutId,
  customerAccessToken: state.customer.customerAccessToken,
  customerCountryCode: state.customer.customerLocation.customerCountryCode,
  customerData: state.customer.data,
  customerDataFetched: state.customer.customerDataFetched,
  customerLocationFetched: state.customer.customerLocationFetched,
  customerZipCode: state.customer.customerLocation.customerZipCode,
  migratedToCartAPIChecked: state.checkout.migratedToCartAPIChecked,
  productCount: selectCartQuantity(state),
  products: state.checkout.localCheckout?.items || [],
  searchValue: state.search.searchValue,
})

const mapDispatch = (dispatch: Dispatch) => ({
  checkIfMigratedToCartAPI: dispatch.checkout.checkIfMigratedToCartAPI,
  fetchAutomaticDiscounts: dispatch.discounts.fetchAutomaticDiscounts,
  fetchCheckout: dispatch.checkout.fetchCheckout,
  fetchCustomerData: dispatch.customer.fetchCustomerData,
  fetchCustomerLocation: dispatch.customer.fetchCustomerLocation,
  logoutCustomer: dispatch.customer.logoutCustomer,
})

export default connect(mapState, mapDispatch)(Layout)
